import { parseScreenshots, type ReportScreenshot } from "./report-screenshots";
// Shared limits and allowlist: diagnostic data never includes request bodies or headers.
export const REPORT_MAX_BYTES = 1024 * 1024;
export type DiagnosticEvent = { at: number; kind: string; message: string };
export type BugReport = {
  id: string;
  message: string;
  screenshots?: ReportScreenshot[];
  diagnostics: null | {
    build: string;
    browser: string;
    viewport: string;
    online: boolean;
    page: string;
    events: DiagnosticEvent[];
  };
};
const kinds = new Set([
  "error",
  "rejection",
  "console.error",
  "console.warn",
  "fetch",
  "resource",
  "connection",
  "websocket",
  "notice",
  "react",
]);
export function redact(value: string): string {
  return value
    .slice(0, 12000)
    .replace(/(?:https?|wss?):\/\/[^\s<>"')]+/gi, (raw) => {
      try {
        const u = new URL(raw);
        // Keep shipped bundle names and line/column numbers for usable stacks.
        if (/^\/assets\/[\w.-]+\.(?:js|mjs)(?::\d+){0,2}$/.test(u.pathname))
          return `${u.protocol}//${u.host}${u.pathname}`;
        return `${u.protocol}//${u.host}/[redacted]`;
      } catch {
        return "[URL removed]";
      }
    })
    .replace(/(?:bearer|basic)\s+[^\s,"']+/gi, "[credential removed]")
    .replace(
      /\b(?:token|password|secret|authorization|cookie|api[_-]?key|grant|sessionid)\b["']?\s*[:=]\s*["']?[^\s,;"']+/gi,
      "[credential removed]",
    )
    .replace(
      /\beyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)?/g,
      "[token removed]",
    )
    .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email removed]")
    .replace(/[A-Za-z0-9_+/-]{40,}={0,2}/g, "[opaque value removed]")
    .replace(/([/?#])[^\s<>"']*[?&#][^\s<>"']*/g, "$1[parameters removed]");
}
export function diagnosticRoute(raw: string, origin: string): string {
  try {
    const u = new URL(raw, origin);
    // Third-party paths can contain identity, file names or tokens.
    if (u.host !== new URL(origin).host)
      return `${u.protocol}//${u.host}/[external]`;
    const p = u.pathname;
    if (/^\/api\/rooms\/[^/]+\/sync$/.test(p)) return "/api/rooms/:room/sync";
    if (/^\/api\/rooms\/[^/]+\/files/.test(p))
      return "/api/rooms/:room/files/:resource";
    if (/^\/api\/rooms(?:\/|$)/.test(p)) return "/api/rooms/:resource";
    if (
      /^\/api\/(session|logout|profile|actions|updates|bug-reports)(?:\/|$)/.test(
        p,
      )
    )
      return p.split("/").slice(0, 3).join("/");
    if (/^\/assets\/[\w.-]+\.(js|css)$/.test(p)) return p;
    return "/[redacted]";
  } catch {
    return "[unknown URL]";
  }
}
export function parseBugReport(input: unknown): BugReport {
  if (!input || typeof input !== "object") throw new Error("Invalid report");
  const v = input as Record<string, unknown>;
  if (typeof v.id !== "string" || !/^[a-f0-9-]{36}$/.test(v.id))
    throw new Error("Invalid report reference");
  if (
    typeof v.message !== "string" ||
    !v.message.trim() ||
    v.message.length > 5000
  )
    throw new Error("Describe the problem in 1–5,000 characters.");
  let diagnostics: BugReport["diagnostics"] = null;
  if (v.diagnostics !== null) {
    if (!v.diagnostics || typeof v.diagnostics !== "object")
      throw new Error("Invalid diagnostics");
    const d = v.diagnostics as Record<string, unknown>;
    const text = (key: string, max: number) => {
      if (typeof d[key] !== "string" || d[key].length > max)
        throw new Error("Invalid diagnostics");
      return redact(d[key]);
    };
    if (
      typeof d.online !== "boolean" ||
      !Array.isArray(d.events) ||
      d.events.length > 80
    )
      throw new Error("Invalid diagnostics");
    diagnostics = {
      build: text("build", 150),
      browser: text("browser", 300),
      viewport: text("viewport", 50),
      online: d.online,
      page: text("page", 100),
      events: d.events.map((e: unknown) => {
        if (!e || typeof e !== "object")
          throw new Error("Invalid diagnostic event");
        const x = e as Record<string, unknown>;
        if (
          typeof x.at !== "number" ||
          !Number.isFinite(x.at) ||
          typeof x.kind !== "string" ||
          !kinds.has(x.kind) ||
          typeof x.message !== "string" ||
          x.message.length > 1000
        )
          throw new Error("Invalid diagnostic event");
        return { at: x.at, kind: x.kind, message: redact(x.message) };
      }),
    };
  }
  return {
    id: v.id,
    message: v.message.trim(),
    diagnostics,
    ...(v.screenshots === undefined
      ? {}
      : { screenshots: parseScreenshots(v.screenshots) }),
  };
}
